import _ from "lodash";

import {
  CategoryId,
  categoryGroups,
  CATEGORY_ENDPOINTS,
  issueDetectors,
} from "../../constants";
import {
  getIncomeAndCashRunwayData,
  isBackgroundReportIncomplete,
} from "../../queryHelpers";
import { StatusType } from "../../StatusIcon";

import { ModalSection } from "../components/ApplicantDetailModal";
import { ApplicantCardDetailsProps } from "../components/ApplicantGroupCard";

import {
  getCreditHistoryData,
  getCriminalHistoryData,
  getRentalHistoryData,
} from "../helpers/applicantHistoryHelpers";

import {
  getIdVerificationModalDetails,
  getIncomeModalDetails,
  getCreditHistoryModalDetails,
  getRentalHistoryModalDetails,
  getCriminalHistoryModalDetails,
} from "./applicantSummaryDetailHelpers";

/**
 * Helper functions to determine the status of different parts of an application
 */

/**
 * Get identity status based on lumen results and identity data
 */
export const getIdentityApplicantSummary = (
  legacyApp: any
): {
  documentDetails: string;
} => {
  const identityData = legacyApp?.identity;
  const identitySkipped = legacyApp?.identity?.skipped;

  let documentDetails = "Pending";

  if (identityData?.documentType) {
    documentDetails = `${identityData.country} ${identityData.documentType}`;
  } else if (identitySkipped) {
    documentDetails = "Skipped";
  }

  return {
    documentDetails,
  };
};

/**
 * Get credit status based on lumen results and credit data
 */
export const getCreditApplicantSummary = (
  applicantLumenResult: any,
  application: any,
  isLumenAvailable: boolean,
  isBackgroundCreditCheckSkipped: boolean,
  isThinCreditFile: boolean | undefined,
  isLowCreditScore: boolean | undefined
): {
  creditScore: string;
  bankruptcies: string;
  collections: string;
  foreclosures: string;
} => {
  const { backgroundCreditCheck, backgroundCreditCheckCompleted } =
    application || {};
  const { creditScoreSummary } = backgroundCreditCheck || {};
  const creditHistoryData = getCreditHistoryData(
    applicantLumenResult,
    creditScoreSummary
  );

  let creditScore = creditScoreSummary?.score;
  if (isThinCreditFile) {
    creditScore = `${creditScore} (Thin Credit File)`;
  } else if (isLowCreditScore) {
    creditScore = `${creditScore} (Low Credit Score)`;
  }
  let creditScoreDisplayValue = "Pending";
  if (creditScore) {
    creditScoreDisplayValue = creditScore;
  } else if (isBackgroundCreditCheckSkipped) {
    creditScoreDisplayValue = "Skipped";
  }
  let bankruptcies = "Pending";
  if (isBackgroundCreditCheckSkipped) {
    bankruptcies = "Skipped";
  } else if (isLumenAvailable) {
    bankruptcies = creditHistoryData.bankruptcies.hasRecords ? "Yes" : "No";
  }
  let collections = "Pending";
  if (isBackgroundCreditCheckSkipped) {
    collections = "Skipped";
  } else if (backgroundCreditCheckCompleted) {
    collections = creditHistoryData.collections.hasRecords ? "Yes" : "No";
  }

  let foreclosures = "Pending";
  if (isBackgroundCreditCheckSkipped) {
    foreclosures = "Skipped";
  } else if (isLumenAvailable) {
    foreclosures = creditHistoryData.foreclosures.hasRecords ? "Yes" : "No";
  }
  return {
    creditScore: creditScoreDisplayValue,
    bankruptcies,
    collections,
    foreclosures,
  };
};

/**
 * Get rental history status based on lumen results and rental history data
 */
export const getRentalApplicantSummary = (
  applicantLumenResult: any,
  application: any,
  isLumenAvailable: boolean,
  isBackgroundCreditCheckSkipped: boolean
): {
  evictionRecords: string;
  rentalCollections: string;
} => {
  const { backgroundCreditCheck, backgroundCreditCheckCompleted } =
    application || {};
  const { evictionRecords: applicantEvictionRecords } =
    backgroundCreditCheck || {};
  const rentalHistoryData = getRentalHistoryData(
    applicantLumenResult,
    applicantEvictionRecords?.evictionRecordGroup
  );

  let evictionRecords = "Pending";
  if (isBackgroundCreditCheckSkipped) {
    evictionRecords = "Skipped";
  } else if (backgroundCreditCheckCompleted) {
    evictionRecords = rentalHistoryData.evictionRecords.hasRecords
      ? "Yes"
      : "No";
  }
  let rentalCollections = "Pending";
  if (isBackgroundCreditCheckSkipped) {
    rentalCollections = "Skipped";
  } else if (isLumenAvailable) {
    rentalCollections = rentalHistoryData.rentalCollections.hasIssue
      ? "Yes"
      : "No";
  }

  return {
    evictionRecords,
    rentalCollections,
  };
};

/**
 * Get criminal history status based on lumen results and criminal history data
 */
export const getCriminalApplicantSummary = (
  application: any,
  isBackgroundCreditCheckSkipped: boolean
): {
  criminalRecords: string;
} => {
  const { backgroundCreditCheck, backgroundCreditCheckCompleted } =
    application || {};
  const { criminalRecords: applicantCriminalRecords } =
    backgroundCreditCheck || {};
  const criminalHistoryData = getCriminalHistoryData(
    applicantCriminalRecords?.criminalRecordGroup
  );
  let criminalRecords = "Pending";
  if (isBackgroundCreditCheckSkipped) {
    criminalRecords = "Skipped";
  } else if (backgroundCreditCheckCompleted) {
    criminalRecords = criminalHistoryData.criminalRecords.hasRecords
      ? "Yes"
      : "No";
  }

  return {
    criminalRecords,
  };
};

/**
 * Convert all application statuses to a format compatible with ApplicantCardDetails
 */
export const formatCardDetails = ({
  title,
  status,
  details,
  allDetailsLink,
  cardModalDetails,
}: {
  title: string;
  status: StatusType;
  details: Array<{ label?: string; value: string; status?: string }>;
  allDetailsLink?: string;
  cardModalDetails?: ModalSection[];
}): ApplicantCardDetailsProps => {
  return {
    status: status as StatusType,
    cardTitle: title.toUpperCase(),
    cardDetails: details.map((detail) => ({
      label: detail.label || "",
      value: detail.value,
      status: detail.status === "pass" ? null : detail.status,
    })),
    allDetailsLink,
    cardModalDetails,
  };
};

/**
 * Format income value for display based on submission status
 */
export const formatIncomeBalanceDisplay = (
  income: number | null | undefined,
  type: "Income" | "Balance",
  isSubmitted: boolean
): string => {
  if (income === 0 || income === undefined || income === null) {
    if (!isSubmitted) {
      return "Pending";
    }
    return `${type} could not be verified`;
  }

  // Format income as dollars with cents
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(income);
};

// Helper for checking status of a specific issue type
export const getIssueStatus = (
  issueType: string,
  label: string,
  applicantLumenResult: any,
  applicationLumenResult: any,
  lumenRun: any,
  isLumenAvailable: boolean
) => {
  const detector = issueDetectors[issueType].find((d) => d.label === label);

  if (!detector || !isLumenAvailable) return undefined;

  const outcomes = lumenRun?.outcomes || [];

  const matchingOutcomes = outcomes
    .filter((outcome) => outcome?.name)
    .filter((outcome) =>
      detector.keywords.every((keyword) =>
        outcome.name.toLowerCase().includes(keyword.toLowerCase())
      )
    );

  if (matchingOutcomes.length === 0) return undefined;

  const applicantTriggeredOutcomes = _.get(
    applicantLumenResult,
    "evaluation.triggeredOutcomes",
    []
  );

  const triggeredOutcomeNames = applicantTriggeredOutcomes
    .map((outcome) => (typeof outcome === "string" ? outcome : outcome?.name))
    .filter(Boolean);

  const applicationTriggeredOutcomes =
    applicationLumenResult?.evaluation?.triggeredOutcomes || [];

  const matchingTriggeredOutcome = matchingOutcomes.find((outcome) => {
    const outcomeName = outcome.name;
    return (
      triggeredOutcomeNames.some(
        (name) => name === outcomeName || name.includes(outcomeName)
      ) ||
      applicationTriggeredOutcomes.some(
        (name) => name === outcomeName || name.includes(outcomeName)
      )
    );
  });

  return matchingTriggeredOutcome
    ? matchingTriggeredOutcome.scoreLabelColor?.toLowerCase()
    : undefined;
};

// Helper for determining overall category status
export const determineCategoryStatus = (
  categoryId: string,
  specificStatuses: string[],
  backgroundReportIncomplete: boolean,
  isLumenAvailable: boolean,
  lumenRun: any
) => {
  if (!isLumenAvailable || !lumenRun) {
    return "pending";
  }

  let status = "pass";

  const validStatuses = specificStatuses.filter(Boolean);

  if (validStatuses.length > 0) {
    if (validStatuses.includes("fail")) status = "fail";
    else if (validStatuses.includes("warn")) status = "warn";
  }

  if (backgroundReportIncomplete) {
    const backgroundCategories = [
      CategoryId.CREDIT_HISTORY,
      CategoryId.RENTAL_HISTORY,
      CategoryId.CRIMINAL_HISTORY,
    ];

    if (
      backgroundCategories.includes(categoryId as CategoryId) &&
      status === "pass"
    ) {
      status = "warn";
    }
  }

  return status;
};

// Create ID Verification card
export const createIdVerificationCard = (
  legacyApp: any,
  application: any,
  applicantLumenResult: any,
  applicationLumenResult: any,
  lumenRun: any,
  isLumenAvailable: boolean,
  backgroundReportIncomplete: boolean,
  basePath: string
) => {
  if (!legacyApp?.identity?.enabled) {
    return null;
  }

  const idVerificationStatus = getIssueStatus(
    "identity",
    "ID Verification",
    applicantLumenResult,
    applicationLumenResult,
    lumenRun,
    isLumenAvailable
  );

  const idStatus = determineCategoryStatus(
    CategoryId.ID_VERIFICATION,
    [idVerificationStatus],
    backgroundReportIncomplete,
    isLumenAvailable,
    lumenRun
  );

  const { documentDetails } = getIdentityApplicantSummary(legacyApp);
  const idGroup = _.find(categoryGroups, { id: CategoryId.ID_VERIFICATION });

  const idVerificationModalDetails = getIdVerificationModalDetails(
    legacyApp,
    application,
    idStatus
  );

  return formatCardDetails({
    title: idGroup.name,
    status: idStatus as StatusType,
    details: [
      {
        label: legacyApp?.identity?.status,
        value: documentDetails,
      },
    ],
    allDetailsLink: `${basePath}/${
      CATEGORY_ENDPOINTS[CategoryId.ID_VERIFICATION]
    }`,
    cardModalDetails: idVerificationModalDetails,
  });
};

// Create Income card
export const createIncomeCard = (
  legacyApp: any,
  applicantLumenResult: any,
  applicationLumenResult: any,
  lumenRun: any,
  isLumenAvailable: boolean,
  backgroundReportIncomplete: boolean,
  isSubmitted: boolean,
  basePath: string
) => {
  const incomeStatus = getIssueStatus(
    "income",
    "Income",
    applicantLumenResult,
    applicationLumenResult,
    lumenRun,
    isLumenAvailable
  );

  const { status: statusFromHistory } = getIncomeAndCashRunwayData(
    applicationLumenResult
  );

  const incomeCategoryStatus = determineCategoryStatus(
    CategoryId.INCOME,
    [incomeStatus, statusFromHistory],
    backgroundReportIncomplete,
    isLumenAvailable,
    lumenRun
  );

  const verifiedIncome = legacyApp?.income?.verifiedIncome * 0.01;
  const verifiedBalance = legacyApp?.balance?.balance;
  const incomeDisplay = formatIncomeBalanceDisplay(
    verifiedIncome,
    "Income",
    isSubmitted
  );
  const balanceDisplay = formatIncomeBalanceDisplay(
    verifiedBalance,
    "Balance",
    isSubmitted
  );
  const incomeGroup = _.find(categoryGroups, { id: CategoryId.INCOME });

  // Only show income detail status if it's a warning or failure
  const detailStatus =
    incomeCategoryStatus === "warn" || incomeCategoryStatus === "fail"
      ? incomeCategoryStatus
      : null;

  const incomeModalDetails = getIncomeModalDetails(
    legacyApp,
    incomeDisplay,
    balanceDisplay,
    incomeCategoryStatus
  );

  return formatCardDetails({
    title: incomeGroup.name,
    status: incomeCategoryStatus as StatusType,
    details: [
      {
        label:
          verifiedIncome > 0 || incomeCategoryStatus === "pending"
            ? "Total Income"
            : "",
        value: incomeDisplay,
        status: detailStatus,
      },
      {
        label:
          verifiedBalance > 0 || incomeCategoryStatus === "pending"
            ? "Total Balance"
            : "",
        value: balanceDisplay,
        status: detailStatus,
      },
    ],
    allDetailsLink: `${basePath}/${CATEGORY_ENDPOINTS[CategoryId.INCOME]}`,
    cardModalDetails: incomeModalDetails,
  });
};

// Create Credit History card
export const createCreditHistoryCard = (
  application: any,
  applicantLumenResult: any,
  applicationLumenResult: any,
  lumenRun: any,
  isLumenAvailable: boolean,
  isBackgroundCreditCheckSkipped: boolean,
  backgroundReportIncomplete: boolean,
  basePath: string,
  canAccessSensitiveDealDataInLumen: boolean
) => {
  const noBackgroundReportStatus = getIssueStatus(
    "background",
    "No Background Report",
    applicantLumenResult,
    applicationLumenResult,
    lumenRun,
    isLumenAvailable
  );

  const lowCreditScoreStatus = getIssueStatus(
    "credit",
    "Low Credit Score",
    applicantLumenResult,
    applicationLumenResult,
    lumenRun,
    isLumenAvailable
  );

  const thinCreditFileStatus = getIssueStatus(
    "credit",
    "Thin Credit File",
    applicantLumenResult,
    applicationLumenResult,
    lumenRun,
    isLumenAvailable
  );

  const bankruptciesStatus = getIssueStatus(
    "credit",
    "Bankruptcies",
    applicantLumenResult,
    applicationLumenResult,
    lumenRun,
    isLumenAvailable
  );

  const collectionsStatus = getIssueStatus(
    "credit",
    "Collections",
    applicantLumenResult,
    applicationLumenResult,
    lumenRun,
    isLumenAvailable
  );

  const foreclosuresStatus = getIssueStatus(
    "credit",
    "Foreclosures",
    applicantLumenResult,
    applicationLumenResult,
    lumenRun,
    isLumenAvailable
  );

  const creditStatus = determineCategoryStatus(
    CategoryId.CREDIT_HISTORY,
    [
      lowCreditScoreStatus,
      thinCreditFileStatus,
      bankruptciesStatus,
      collectionsStatus,
      foreclosuresStatus,
    ],
    backgroundReportIncomplete,
    isLumenAvailable,
    lumenRun
  );
  const isThinCreditFile =
    thinCreditFileStatus === "warn" || thinCreditFileStatus === "fail";
  const isLowCreditScore =
    lowCreditScoreStatus === "warn" || lowCreditScoreStatus === "fail";

  const creditGroup = _.find(categoryGroups, {
    id: CategoryId.CREDIT_HISTORY,
  });

  const creditScoreStatus = lowCreditScoreStatus || thinCreditFileStatus;

  const { creditScore, bankruptcies, collections, foreclosures } =
    getCreditApplicantSummary(
      applicantLumenResult,
      application,
      isLumenAvailable,
      isBackgroundCreditCheckSkipped,
      isThinCreditFile,
      isLowCreditScore
    );

  const creditHistoryModalDetails = getCreditHistoryModalDetails(
    applicantLumenResult,
    application,
    isLumenAvailable,
    isBackgroundCreditCheckSkipped,
    isLowCreditScore,
    isThinCreditFile,
    creditScoreStatus,
    bankruptciesStatus,
    collectionsStatus,
    foreclosuresStatus
  );

  return formatCardDetails({
    title: creditGroup.name,
    status: creditStatus as StatusType,
    details: [
      {
        label: "Credit Score",
        value: creditScore,
        status: creditScoreStatus || noBackgroundReportStatus,
      },
      {
        label: "Bankruptcies",
        value: bankruptcies,
        status: bankruptciesStatus || noBackgroundReportStatus,
      },
      {
        label: "Collections",
        value: collections,
        status: collectionsStatus || noBackgroundReportStatus,
      },
      {
        label: "Foreclosures",
        value: foreclosures,
        status: foreclosuresStatus || noBackgroundReportStatus,
      },
    ],
    // When user does not have permission to view background check details,
    // we don't provide the link to the credit-and-background-check
    // page since that page requires authentication and would be inaccessible.
    // This prevents users from seeing a "View all details" link that would lead nowhere.
    ...(!canAccessSensitiveDealDataInLumen
      ? {}
      : {
          allDetailsLink: `${basePath}/${
            CATEGORY_ENDPOINTS[CategoryId.CREDIT_HISTORY]
          }`,
        }),
    // When user does not have permission to view background check details,
    // we don't provide the modal details since the data is restricted.
    ...(!canAccessSensitiveDealDataInLumen
      ? {}
      : {
          cardModalDetails: creditHistoryModalDetails,
        }),
  });
};

// Create Rental History card
export const createRentalHistoryCard = (
  application: any,
  applicantLumenResult: any,
  applicationLumenResult: any,
  lumenRun: any,
  isLumenAvailable: boolean,
  isBackgroundCreditCheckSkipped: boolean,
  backgroundReportIncomplete: boolean,
  basePath: string,
  canAccessSensitiveDealDataInLumen: boolean
) => {
  const noBackgroundReportStatus = getIssueStatus(
    "background",
    "No Background Report",
    applicantLumenResult,
    applicationLumenResult,
    lumenRun,
    isLumenAvailable
  );

  const evictionsStatus = getIssueStatus(
    "rental",
    "Evictions",
    applicantLumenResult,
    applicationLumenResult,
    lumenRun,
    isLumenAvailable
  );

  const rentalCollectionsStatus = getIssueStatus(
    "rental",
    "Rental Collections",
    applicantLumenResult,
    applicationLumenResult,
    lumenRun,
    isLumenAvailable
  );

  const rentalStatus = determineCategoryStatus(
    CategoryId.RENTAL_HISTORY,
    [evictionsStatus, rentalCollectionsStatus],
    backgroundReportIncomplete,
    isLumenAvailable,
    lumenRun
  );

  const rentalGroup = _.find(categoryGroups, {
    id: CategoryId.RENTAL_HISTORY,
  });

  const { evictionRecords, rentalCollections } = getRentalApplicantSummary(
    applicantLumenResult,
    application,
    isLumenAvailable,
    isBackgroundCreditCheckSkipped
  );

  const rentalHistoryModalDetails = getRentalHistoryModalDetails(
    applicantLumenResult,
    application,
    isLumenAvailable,
    isBackgroundCreditCheckSkipped,
    evictionsStatus,
    rentalCollectionsStatus
  );

  return formatCardDetails({
    title: rentalGroup.name,
    status: rentalStatus as StatusType,
    details: [
      {
        label: "Eviction Records",
        value: evictionRecords,
        status: evictionsStatus || noBackgroundReportStatus,
      },
      {
        label: "Rental Collections",
        value: rentalCollections,
        status: rentalCollectionsStatus || noBackgroundReportStatus,
      },
    ],
    // When user does not have permission to view background check details,
    // we don't provide the link to the credit-and-background-check
    // page since that page requires authentication and would be inaccessible.
    // This prevents users from seeing a "View all details" link that would lead nowhere.
    ...(!canAccessSensitiveDealDataInLumen
      ? {}
      : {
          allDetailsLink: `${basePath}/${
            CATEGORY_ENDPOINTS[CategoryId.RENTAL_HISTORY]
          }`,
        }),
    // When user does not have permission to view background check details,
    // we don't provide the modal details since the data is restricted.
    ...(!canAccessSensitiveDealDataInLumen
      ? {}
      : {
          cardModalDetails: rentalHistoryModalDetails,
        }),
  });
};

// Create Criminal History card
export const createCriminalHistoryCard = (
  application: any,
  applicantLumenResult: any,
  applicationLumenResult: any,
  lumenRun: any,
  isLumenAvailable: boolean,
  isBackgroundCreditCheckSkipped: boolean,
  backgroundReportIncomplete: boolean,
  basePath: string,
  canAccessSensitiveDealDataInLumen: boolean
) => {
  const noBackgroundReportStatus = getIssueStatus(
    "background",
    "No Background Report",
    applicantLumenResult,
    applicationLumenResult,
    lumenRun,
    isLumenAvailable
  );

  const criminalRecordsStatus = getIssueStatus(
    "criminal",
    "Criminal Records",
    applicantLumenResult,
    applicationLumenResult,
    lumenRun,
    isLumenAvailable
  );

  const feloniesStatus = getIssueStatus(
    "criminal",
    "Felonies",
    applicantLumenResult,
    applicationLumenResult,
    lumenRun,
    isLumenAvailable
  );

  const criminalStatus = determineCategoryStatus(
    CategoryId.CRIMINAL_HISTORY,
    [criminalRecordsStatus, feloniesStatus],
    backgroundReportIncomplete,
    isLumenAvailable,
    lumenRun
  );

  const { criminalRecords } = getCriminalApplicantSummary(
    application,
    isBackgroundCreditCheckSkipped
  );

  const criminalGroup = _.find(categoryGroups, {
    id: CategoryId.CRIMINAL_HISTORY,
  });

  const criminalHistoryModalDetails = getCriminalHistoryModalDetails(
    application,
    isBackgroundCreditCheckSkipped
  );

  return formatCardDetails({
    title: criminalGroup.name,
    status: criminalStatus as StatusType,
    details: [
      {
        label: "Criminal Records",
        value: criminalRecords,
        status: criminalRecordsStatus || noBackgroundReportStatus,
      },
    ],
    // When user does not have permission to view background check details,
    // we don't provide the link to the credit-and-background-check
    // page since that page requires authentication and would be inaccessible.
    // This prevents users from seeing a "View all details" link that would lead nowhere.
    ...(!canAccessSensitiveDealDataInLumen
      ? {}
      : {
          allDetailsLink: `${basePath}/${
            CATEGORY_ENDPOINTS[CategoryId.CRIMINAL_HISTORY]
          }`,
        }),
    // When user does not have permission to view background check details,
    // we don't provide the modal details since the data is restricted.
    ...(!canAccessSensitiveDealDataInLumen
      ? {}
      : {
          cardModalDetails: criminalHistoryModalDetails,
        }),
  });
};

// Main function to get all card details by category
export const getCardDetailsByCategory = (
  lumenRun: any,
  legacyApp: any,
  application: any,
  applicantLumenResult: any,
  applicationLumenResult: any,
  isLumenAvailable: boolean,
  isSubmitted: boolean,
  showBackgroundCreditSection: boolean,
  canAccessSensitiveDealDataInLumen: boolean
): Record<string, ApplicantCardDetailsProps> => {
  const cardDetailsByCategory: Record<string, ApplicantCardDetailsProps> = {};
  const basePath = `/applications/${legacyApp?.appId}`;
  const backgroundReportIncomplete = isBackgroundReportIncomplete(lumenRun);
  const isBackgroundCreditCheckSkipped = legacyApp?.credit?.skipped;

  // ID Verification card
  const idVerificationCard = createIdVerificationCard(
    legacyApp,
    application,
    applicantLumenResult,
    applicationLumenResult,
    lumenRun,
    isLumenAvailable,
    backgroundReportIncomplete,
    basePath
  );
  if (idVerificationCard) {
    cardDetailsByCategory[CategoryId.ID_VERIFICATION] = idVerificationCard;
  }

  // Income card
  cardDetailsByCategory[CategoryId.INCOME] = createIncomeCard(
    legacyApp,
    applicantLumenResult,
    applicationLumenResult,
    lumenRun,
    isLumenAvailable,
    backgroundReportIncomplete,
    isSubmitted,
    basePath
  );

  // Background check sections
  if (showBackgroundCreditSection) {
    // Credit History card
    cardDetailsByCategory[CategoryId.CREDIT_HISTORY] = createCreditHistoryCard(
      application,
      applicantLumenResult,
      applicationLumenResult,
      lumenRun,
      isLumenAvailable,
      isBackgroundCreditCheckSkipped,
      backgroundReportIncomplete,
      basePath,
      canAccessSensitiveDealDataInLumen
    );

    // Rental History card
    cardDetailsByCategory[CategoryId.RENTAL_HISTORY] = createRentalHistoryCard(
      application,
      applicantLumenResult,
      applicationLumenResult,
      lumenRun,
      isLumenAvailable,
      isBackgroundCreditCheckSkipped,
      backgroundReportIncomplete,
      basePath,
      canAccessSensitiveDealDataInLumen
    );

    // Criminal History card
    cardDetailsByCategory[CategoryId.CRIMINAL_HISTORY] =
      createCriminalHistoryCard(
        application,
        applicantLumenResult,
        applicationLumenResult,
        lumenRun,
        isLumenAvailable,
        isBackgroundCreditCheckSkipped,
        backgroundReportIncomplete,
        basePath,
        canAccessSensitiveDealDataInLumen
      );
  }

  return cardDetailsByCategory;
};
